// Module `experts` (API-v3 9): staff pool/profile/timesheets/assignments/
// selection/contract and the expert portal. Owned by WP experts.
import { z } from "zod";
import { ASSIGNMENT_ROLES, EXPERT_KINDS, EXPERT_LEVELS, WORK_PLACES, type AssignmentRole, type TimesheetStatus, type WorkPlace } from "../enums";
import { LIMITS } from "../limits";
import { uuid } from "../schemas/common";
import type { ActivityApproval } from "./activities";
import type { MailStatus } from "./mail";
import {
  centsV3,
  country,
  days,
  email,
  isoDate,
  isoMonth,
  text,
  textOrEmpty,
  textOrNull,
  type FileRef,
  type HistoryEntry,
  type Id,
  type ProjectRef,
  type StatusChip,
  type Steps,
  type Tone,
} from "./common";

export const expertKind = z.enum(EXPERT_KINDS);
export const expertLevel = z.enum(EXPERT_LEVELS);
export const workPlace = z.enum(WORK_PLACES);
export const assignmentRole = z.enum(ASSIGNMENT_ROLES);

// ---------- 9.1 pool, profile, add/edit, import ----------

/** One expert of the pool (Experts wireframe, round 4). */
export type ExpertsOverviewRow = {
  engagementId: Id;
  name: string;
  /** "KE 1 · Bulgaria", "NKE · Senior · France" */
  sub: string;
  kind: "ke" | "nke";
  category: string;
  /** Role in the team (the position title), or null. */
  role: string | null;
  /** Someone with the expert's email address is on the project team. */
  alsoTeamMember: boolean;
  /** OB-F17 (round 4): a key expert whose CV the setup checklist counts as missing (Experts' `?filter=cv_missing`). */
  cvMissing?: boolean;
  expertise: string | null;
  daysUsed: number;
  dayBudget: number;
  /** EV4: days planned and not used yet (assignments, and experts named in a ToR with the funder or approved). */
  daysPlanned?: number;
  /** Audit round 4 (EX-F2): days on work still waiting for an approved ToR, not counted as planned. */
  daysWaiting?: number;
  feeCents: number | null;
  /** Days used x fee per day. */
  feesSpentCents: number;
  status: StatusChip;
  /** What the team does next for this expert ("August timesheet to sign", "2 days left"), or null. */
  nextStep: { label: string; tone: Tone } | null;
  /** The timesheet now in circulation (last month): "20.0 days · to sign". */
  thisMonth: { month: string; days: number; label: string; tone: Tone } | null;
  path: string;
};
export type ExpertsOverview = {
  tiles: {
    /** Key experts in the contract (contracted). */
    keyExperts: number;
    /** EX-N1: key experts proposed to the funder, not in the contract yet. */
    keyProposed?: number;
    /** Key experts also on the project team. */
    keyAlsoTeam: number;
    nonKeyExperts: number;
    nonKeyContracted: number;
    /** Non-key experts in the pool, not contracted (candidates in a selection are not in the pool: EX9). */
    nonKeyInPool: number;
    /** Of contracted experts. */
    daysUsed: number;
    daysBudget: number;
    /** EV4: of contracted experts, days planned and not used yet. */
    daysPlanned?: number;
    feesSpentCents: number;
    /** EX8: the contract's fee lines (lines experts are contracted on, or named for fees or experts); without budget lines, contracted days x fee per day. feesSpentCents is what is spent on those lines. */
    feesContractCents: number;
    /** Where feesContractCents comes from, for the figure's sub-line: "on fee lines 1–13", "on fee line 4", "in the experts' contracts". */
    feesSource?: string;
    timesheetsToSign: number;
  };
  attention: {
    timesheetsToSign: number;
    closeToBudget: { name: string; used: number; budget: number; path: string }[];
    /** Fee invoices sent by experts, not checked yet. */
    invoicesToCheck: number;
    /** Audit round 4 (EX-F2): contracted experts whose used + planned + waiting days pass their contract days. */
    overContract?: { name: string; over: number; path: string }[];
    /** Service agreements, addenda and confidentiality agreements sent and not signed. */
    contractsToSign: { name: string; path: string }[];
  };
  rows: ExpertsOverviewRow[];
};
export const expertBody = z.object({
  name: text(LIMITS.expertName, "Name"),
  email,
  country: country.nullable(),
  kind: expertKind,
  level: expertLevel,
  keNumber: z.number().int().min(1).max(99).nullable(),
  positionTitle: textOrNull(LIMITS.positionTitle),
  expertise: textOrEmpty(LIMITS.expertise),
  dayBudget: days(),
  /** Optional: a person without "See money" sends none (plan 2026-11-03). */
  feeCents: centsV3(LIMITS.feeRateCentsMax).nullable().optional(),
  cvFileId: uuid.nullable(),
  /**
   * EX5: a key expert already named in the signed contract (the project's setup)
   * is contracted at once. Otherwise a new key expert starts as "Proposed" until
   * the funder agrees to an expert change request.
   */
  namedInContract: z.boolean().optional(),
});
export type ExpertBody = z.infer<typeof expertBody>;
export const expertPatch = expertBody.partial();
/** #9: the team confirms the expert account that matches the profile's email. */
export const linkAccountBody = z.object({ userId: uuid });
export type TimesheetLine = {
  id: Id;
  date: string;
  endDate: string | null;
  dateLabel: string;
  work: string;
  place: WorkPlace;
  placeLabel: string;
  days: number;
  source: "manual" | "assignment" | "event" | "mission" | "meeting" | "import";
  origin: string | null;
  locked: boolean;
  activity: { id: Id; code: string } | null;
  corrections: { reason: string; by: string; at: string }[];
};
export type ExpertDetail = {
  engagementId: Id;
  expertId: Id;
  name: string;
  status: StatusChip;
  /** EX-F5: a replacement of this expert asked for in a contract amendment, with the link to it. */
  replacement?: { label: string; path: string } | null;
  /** EX-F2: days over the contract once everything planned and waiting is used (0 when it fits). */
  overContractDays?: number;
  /** OB-F11: the first month with a timesheet ("2027-06") when the work has not started yet; null once it has. */
  timesheetsFrom?: string | null;
  subtitle: string;
  tiles: {
    dayBudget: string;
    contractNumber: string | null;
    daysUsed: string;
    usedPct: number;
    feePerDay: string;
    timesheet: { month: string; days: number; status: StatusChip };
  };
  /** counts: the days count as planned (false: waiting for approval); replacement: still to come while a replacement is asked for (EX-F5). */
  assignments: { code: string; activity: string; sub: string; path: string; dates: string; days: number; status: StatusChip; counts?: boolean; replacement?: boolean }[];
  timesheet: {
    month: string;
    status: StatusChip;
    lines: TimesheetLine[];
    totalDays: number;
    canSend: boolean;
    /** Waiting for his signature: "Remind him to sign" (one reminder a day). */
    canRemind: boolean;
    remindedToday: boolean;
  };
  profile: { email: string; phone: string | null; languages: string[]; cv: FileRef | null; legacyCvUrl: string | null; inPoolSince: string };
  history: HistoryEntry[];
  form: ExpertBody;
  /** Bank details, always masked; owners and admins may mark a changed account as checked. */
  bank?: { ibanMasked: string | null; pendingMasked: string | null; pending: boolean; canVerify: boolean };
  // ----- round 4 record (Expert wireframe) -----
  /** "Non-key expert · Senior · France · called in per activity, each assignment with an approved ToR" */
  headline: string;
  /** The expertise as tags ("Border management", "Risk analysis"). */
  expertise: string[];
  figures: {
    daysUsed: number;
    dayBudget: number;
    /** Assignment days planned and not used yet. */
    plannedDays: number;
    /** EX-F2: days on work still waiting for an approved ToR (not in plannedDays). */
    waitingDays?: number;
    feesSoFarCents: number;
    feePerDay: string;
    perDiems: string;
    contract: { number: string | null; signedOn: string | null; endsOn: string | null };
  };
  notes: ExpertNote[];
  canAddNote: boolean;
  money: { feePerDay: string; perDiems: string; invoicesPaid: number; invoicesToCheck: number; claims: { label: string; status: StatusChip }[] };
  /** CV, signed contract documents, signed timesheets. `url` downloads a file; `path` opens a page. */
  documents: { kind: "PDF" | "XLS" | "DOC"; label: string; url: string | null; path: string | null }[];
  portal: { hasAccount: boolean; lastSignedIn: string | null };
  /** The stakeholder record with the same email address, if any. */
  stakeholderPath: string | null;
  /** EX5: a proposed key expert: the change request that asks the funder (or the one under way). */
  changeRequest?: { label: string; path: string } | null;
  /** #9: an expert account with this profile's (verified) email, not linked yet; the team confirms the link. */
  accountMatch?: { userId: Id; name: string; email: string; signedUpOn: string } | null;
};
export type ExpertNote = { id: Id; body: string; author: string; at: string; canEdit: boolean };
export const expertNoteBody = z.object({ body: text(4_000, "Note") });
export const cvReadBody = z.object({ fileId: uuid });
export type CvReading = { name: string | null; email: string | null; languages: string[]; expertise: string | null; level: "senior" | "junior" | null };
export const cvImportBody = z.object({ fileIds: z.array(uuid).min(1).max(LIMITS.cvBatchMax) });
export type CvImportPreview = {
  previewId: Id;
  rows: { rowId: string; fileName: string; name: string; expertise: string; languages: string[]; alreadyInPool: boolean; add: boolean }[];
};
export const cvImportConfirmBody = z.object({ rowIds: z.array(z.string().max(60)).max(LIMITS.cvBatchMax) });

// ---------- 9.2 timesheets, corrections, assignments, workload ----------

export type TimesheetMonth = {
  month: string;
  months: { value: string; label: string }[];
  rows: {
    engagementId: Id;
    name: string;
    category: string;
    /** "KE 1 · Team leader", "NKE · Senior" */
    sub: string;
    days: number;
    prefilledFrom: string;
    status: StatusChip;
    path: string;
    /** Waiting for signature and not reminded today. */
    canRemind: boolean;
  }[];
  totalDays: number;
  /** "2 of 6 signed" */
  summary: { signed: number; total: number };
  /**
   * IX-F11 (feedback round 3): `waiting` counts the timesheets waiting for a signature in every month
   * (the same figure as the Expert pool and its tab); `waitingThisMonth` those of the month shown.
   */
  /** signed: signed timesheets of the month with days in them (EX-F6: ready for the invoice); invoicesToCheck: fee invoices sent and not checked, the Expert pool's figure. */
  attention: { waiting: number; waitingThisMonth?: number; drafts: { count: number; first: { name: string; path: string } | null }; signed: number; invoicesToCheck?: number };
  /** How many "Remind N to sign" would remind now. */
  toRemind: number;
};
export type TimesheetDetail = {
  engagementId: Id;
  month: string;
  status: TimesheetStatus;
  statusChip: StatusChip;
  line: string;
  lines: TimesheetLine[];
  totalDays: number;
  signature: { signedAt: string; signer: string; hash: string } | null;
  /** The newest open question, as one text (older screens); the questions themselves are in `questions`. */
  question: { text: string; at: string } | null;
  /** Feedback round 3 (EP-F2, IX-F2): the expert's questions, per line or about the month, with the team's answers. */
  questions: TimesheetQuestionV3[];
  /** Questions the team has not answered or resolved yet: while > 0 the expert cannot sign and the team cannot send it again. */
  openQuestions: number;
  /** IX-F4: lines the team removed, with the reason (shown to both sides). */
  removedLines: { label: string; days: number; reason: string; by: string; at: string }[];
  paperFile: FileRef | null;
  // staff view (optional additions)
  timesheetId?: Id | null;
  expert?: { name: string; path: string };
  canSend?: boolean;
  canApprove?: boolean;
  canPaper?: boolean;
  /** Staff may add a line by hand (the timesheet is not signed yet). */
  canAddLine?: boolean;
  /** The latest "ready to sign" email: where it went and what happened to it (EM3). */
  mail?: MailStatus | null;
};
/** One question of the expert about a timesheet (EP-F2), with the team's answer (IX-F2). */
export type TimesheetQuestionV3 = {
  id: Id;
  /** The line it is about; null = about the whole month (or the line was removed). */
  lineId: Id | null;
  /** "19–22 Oct, Expert mission A3.1": the line as it was asked about. */
  lineLabel: string | null;
  text: string;
  askedAt: string;
  answer: { text: string; by: string; at: string } | null;
  /** null = open. */
  resolution: "answered" | "corrected" | "removed" | "resolved" | null;
  /** "Corrected by Laura Jansen on 31 Oct 2026", or null while open. */
  resolvedLabel: string | null;
};
/** GET /timesheet-lines/:lineId */
export type TimesheetLineDetail = TimesheetLine & {
  timesheetPath: string;
  month: string;
  expertName: string;
  engagementId: Id;
  /** IX-F4: the expert's questions about this line, shown on the correction page. */
  questions?: TimesheetQuestionV3[];
  /** IX-F4: why "Remove this line" is not offered (a line from an assignment), else null. */
  removeBlocked?: string | null;
};
/** POST /timesheet-lines/:lineId/remove (IX-F4): the team removes a line, with the reason (kept in the timesheet's history). */
export const removeLineBody = z.object({ reason: text(1_000, "Reason") });
/** POST .../timesheets/:month/questions/:questionId (IX-F2, IX-F3): the team answers a question, or marks it resolved. */
export const answerQuestionBody = z.discriminatedUnion("op", [
  z.object({ op: z.literal("answer"), text: text(4_000, "Answer") }),
  z.object({ op: z.literal("resolve") }),
]);
export type AnswerQuestionBody = z.infer<typeof answerQuestionBody>;
export const monthQuery = z.object({ month: isoMonth.optional() });
/** POST .../timesheets/:month/send: "1" sends a timesheet with no days after the warning (EX12). */
export const sendTimesheetQuery = z.object({ confirmEmpty: z.literal("1").optional() });
/** POST .../timesheets/:month/lines: a line added by hand by the project team (EX3). */
export const addLineBody = z.object({
  date: isoDate,
  endDate: isoDate.nullable(),
  days: days(31),
  place: workPlace,
  work: text(LIMITS.workNote, "Work"),
  activityId: uuid.nullable(),
});
export type AddLineBody = z.infer<typeof addLineBody>;
export const correctLineBody = z.object({
  date: isoDate,
  endDate: isoDate.nullable(),
  days: days(31),
  place: workPlace,
  work: textOrEmpty(LIMITS.workNote),
  reason: text(1_000, "Reason"),
});
export type AssignmentRow = {
  /** "programme:<engagement>:<activity>" for a programme role without its own assignment. */
  id: Id;
  expert: { name: string; category: string; path: string; sub?: string; kind?: "ke" | "nke" };
  code: string;
  activity: { title: string; path: string; status: StatusChip };
  /** "moderator" only for a programme role without its own assignment. */
  role: AssignmentRole | "moderator";
  roleLabel: string;
  days: number;
  // round 4 (Assignments wireframe); optional so the POST answer stays as it was
  /** "29 Sep–2 Oct" */
  dates?: string;
  startDate?: string | null;
  endDate?: string | null;
  /** The assignment's status: ToR approved, In review, Changes requested on the ToR, Done... (X-28: never where the row comes from). */
  status?: StatusChip;
  /** For the status filter. */
  group?: "approved" | "waiting" | "done";
  /** The row comes from an event programme (shown as a tag, not as the status). */
  fromProgramme?: boolean;
  removable?: boolean;
  /** EX-F2: the days count as planned (the activity's ToR or status allows it); false = waiting for approval. */
  counts?: boolean;
  /** EX-F5: a replacement of this expert is asked for; set on rows still to come. */
  replacement?: { label: string; path: string };
};
export type AssignmentsList = {
  rows: AssignmentRow[];
  attention: {
    changesRequested: { count: number; first: { label: string; path: string } | null };
    /** Assignment days planned and not used yet, of contracted non-key experts (approved work only). */
    nkePlannedDays: number;
    /** EX-F2: days of contracted non-key experts on work still waiting for an approved ToR. */
    nkeWaitingDays?: number;
    /** Days left in the contracts of contracted non-key experts. */
    nkeDaysLeft: number;
    /** EX-F4: programme rows still to come (the rows the list shows by default). */
    fromProgramme: { count: number; first: { label: string; path: string } | null };
    /** EX-F2: contracted experts whose used + planned + waiting days pass their contract days. */
    overContract?: { count: number; first: { name: string; over: number; path: string } | null };
  };
};
export type AssignOptions = {
  expert: { name: string; daysLeft: number; dayBudget: number };
  /** approval: D6 "Needs the funder's approval" of each activity, for the switch above the picker. */
  activities: {
    id: Id;
    code: string;
    title: string;
    approval: ActivityApproval;
    /**
     * EX7 (Marius round 5): where the days go in the activity's ToR. `newVersion`:
     * the ToR is signed off, with the funder or approved, so this expert joins
     * only its next version.
     */
    tor?: { note: string; newVersion: boolean; path: string | null };
  }[];
  roles: { value: AssignmentRole; label: string }[];
  /** D6: the project default (service contract on, grant off). */
  approvalDefault: boolean;
};
export const assignBody = z.object({ activityId: uuid, role: assignmentRole, days: days() });
export type Workload = {
  months: { value: string; label: string }[];
  limit: number;
  rows: {
    name: string;
    category: string;
    path: string;
    /** "20 days/mo" (average over the months shown) */
    thisProject: string;
    otherProjects: string;
    perMonth: number[];
    load: StatusChip;
    // round 4 (Expert Workload wireframe)
    /** "KE 1", "NKE · Senior" */
    sub?: string;
    /** Shown as the tag "Also on this team". */
    alsoTeamMember?: boolean;
    /** Bar colour per month: under (limit − 4) days, up to the limit, over the limit. */
    levels?: ("low" | "mid" | "high")[];
  }[];
  attention?: {
    over: { count: number; first: { name: string; days: number; month: string; path: string } | null };
    /** Experts under (limit − 4) days in the second month shown. */
    available: { count: number; month: string };
    /** Other projects that share experts with this one ("Other project" when the viewer cannot open it). */
    shared: { count: number; first: string | null };
  };
};
export const workloadQuery = z.object({ from: isoMonth.optional(), months: z.coerce.number().int().min(1).max(12).default(3) });

// ---------- 9.3 selection and expert contract ----------

export const needBody = z.object({
  title: text(200, "Title"),
  activityId: uuid.nullable(),
  resultId: uuid.nullable(),
  level: expertLevel,
  days: days(),
  startDate: isoDate.nullable(),
  requirements: z.array(text(500, "Requirement")).max(20),
  assets: z.array(text(500, "Asset")).max(20),
});
export type NeedBody = z.infer<typeof needBody>;
export type NeedRow = {
  id: Id;
  title: string;
  sub: string;
  for: string;
  candidates: number;
  status: StatusChip;
  path: string;
  // round 4 (Expert Selection wireframe)
  /** "20 days · from Nov 2026 · for A1.4 Data-sharing protocol · 4 candidates" */
  line?: string;
  /** Need · Candidates · Shortlist · Declaration · Contract */
  track?: { label: string; state: "done" | "current" | "todo" }[];
  /** The one next action: compare (open the need), remind (the declaration), contract (see it). */
  action?: { kind: "compare" | "remind" | "contract"; label: string; path: string } | null;
  /** X-52 (round 4): "Starts in 23 days – no expert contracted yet" (the lead-time rule of AR-F8); null when there is time. */
  leadTime?: string | null;
};
export type NeedsList = {
  rows: NeedRow[];
  attention: {
    /** Needs being shortlisted that have candidates (the first one's count and link). */
    shortlist: { count: number; candidates: number; path: string | null };
    declarationsMissing: { count: number; path: string | null };
    /** Experts of the organisation's pool not contracted or shortlisted on this project. */
    poolExperts: number;
  };
  /** EX9: the choices of the "New expert need" form (the need is made on save). */
  options?: { activities: { id: Id; code: string; title: string }[]; results: { id: Id; code: string; title: string }[] };
};
export type NeedDetail = NeedBody & {
  id: Id;
  status: StatusChip;
  subLine: string;
  steps: Steps;
  candidates: {
    id: Id;
    name: string;
    sub: string;
    path: string;
    source: string;
    fit: string;
    cvChecked: boolean;
    available: string;
    actions: ("select" | "compare" | "check_cv")[];
    /** The address the declaration goes to; null = none yet (Select is refused). */
    email?: string | null;
  }[];
  // optional additions
  forLabel?: string;
  contractPath?: string | null;
  poolOptions?: { expertId: Id; name: string; sub: string }[];
  options?: { activities: { id: Id; code: string; title: string }[]; results: { id: Id; code: string; title: string }[] };
};
/** An email address, or "" / null for "not known yet" (the API stores null). Left out = unchanged. */
const emailOrNone = z.union([email, z.literal("")]).nullable().optional();
export const addCandidateBody = z.union([
  z.object({ expertId: uuid }),
  z.object({
    name: text(LIMITS.expertName, "Name"),
    /** Left empty: read from the CV when it has one (EX2). */
    email: emailOrNone,
    level: expertLevel,
    country: country.nullable(),
    cvFileId: uuid,
    source: z.enum(["cv_import", "partner", "call"]),
  }),
]);
export const verdict = z.object({ requirement: text(500, "Requirement"), evidence: textOrEmpty(2_000), meets: z.enum(["yes", "no", "unknown"]) });
export const candidatePatch = z.object({
  email: emailOrNone,
  verdicts: z.array(verdict).max(40).optional(),
  assessment: textOrEmpty(10_000).optional(),
  feeAskedCents: centsV3(LIMITS.feeRateCentsMax).nullable().optional(),
  availableFrom: isoDate.nullable().optional(),
});
export type CandidateDetail = {
  id: Id;
  name: string;
  sub: string;
  cv: FileRef | null;
  verdicts: z.infer<typeof verdict>[];
  assessment: string;
  feeAskedCents: number | null;
  availableFrom: string | null;
  comparedWith: { name: string; fit: string; feeAsked: string; available: string; workedWithUs: boolean }[];
  status: string;
  // optional additions
  needId?: Id;
  needTitle?: string;
  canSelect?: boolean;
  /** Where the declaration goes: the candidate's, else the pool expert's address (EX2). */
  email?: string | null;
  /** Why "Select" cannot send the declaration yet (no email address), else null. */
  selectBlocked?: string | null;
};
export const contractTermsBody = z.object({
  feeCents: centsV3(LIMITS.feeRateCentsMax),
  days: days(),
  periodStart: isoDate,
  periodEnd: isoDate,
  budgetLineId: uuid.nullable(),
  documents: z.object({ serviceAgreement: z.boolean(), confidentiality: z.boolean(), proofOfQualifications: z.boolean() }),
  proofFileId: uuid.nullable(),
});
export type ContractTermsBody = z.infer<typeof contractTermsBody>;
export type ExpertContract = {
  title: string;
  subLine: string;
  steps: Steps;
  declaration: { sentAt: string | null; status: StatusChip };
  terms: ContractTermsBody;
  feeCap: { cents: number | null; warning: string | null };
  budgetLines: { id: Id; label: string }[];
  canSend: boolean;
  sendReason: string | null;
  engagementPath: string | null;
  // optional additions
  proofFile?: FileRef | null;
  sent?: { at: string; documents: string[] } | null;
};

// ---------- 9.4 expert portal ----------

export type PortalHome = {
  expert: { firstName: string; name: string };
  project: ProjectRef;
  projects: ProjectRef[];
  /**
   * "Needs your attention" (EP-F5, as on the team's Home): each to-do with its due date (null = none)
   * and the words of its one action ("Check and sign").
   */
  todo: { label: string; detail: string; path: string; dueOn?: string | null; action?: string }[];
  /** EP-F5: "Non-key expert · Contract NKE-07 · until 30 Jun 2028" under the greeting. */
  headline?: string;
  /** EP-F5 "At a glance": this month's days against the working days, the last invoice or claim. */
  glance?: {
    month: { month: string; label: string; filed: number; planned: number; workingDays: number };
    lastInvoice: { label: string; status: StatusChip } | null;
  };
  /** EP-F5 "Coming up": the next assignments, soonest first. */
  comingUp?: { date: string; endDate: string | null; code: string; title: string; role: string; days: number; status: StatusChip }[];
  tiles: {
    daysLeft: string;
    /** EX4: the figures of the staff's Expert record, from the one days calculation. */
    days?: { used: number; planned: number; budget: number; left: number };
    contractNumber: string | null;
    timesheet: { month: string; days: number; status: StatusChip };
    nextAssignment: { title: string; dates: string } | null;
  };
  /** EP-F5: the activity's title only; role, status and the dates with the year in their own fields; Upcoming or Done. */
  assignments: { code: string; activity: string; status: string; dates: string; days: number; role?: string; statusChip?: StatusChip; group?: "upcoming" | "done" }[];
  timesheets: { month: string; label: string; days: number; status: StatusChip; action: "sign" | "view" }[];
  /** EP-F5: with the kind (ToR, Contract, CV), the date and the status. */
  documents: { label: string; url: string; kind?: "ToR" | "Contract" | "CV" | "Document"; date?: string | null; status?: string | null }[];
  cv: { fileName: string | null; uploadedAt: string | null; languages: string[] };
  /** EX-N4 (retest 1 Nov): the expert's travel claims, as the portal's invoices list shows them. */
  claims: PortalInvoices["rows"];
  readOnly: boolean;
};
export const portalProjectBody = z.object({ projectId: uuid });
export type PortalHours = {
  month: string;
  label: string;
  open: boolean;
  /**
   * EP-F11: `filed` counts only days that have passed (and, for days from an assignment, whose ToR is
   * approved); the rest of the month's lines are `planned`.
   */
  tiles: { filed: number; planned?: number; available: number; daysLeft: string; contractNumber: string | null; status: StatusChip; signAfter: string };
  activities: { id: Id; code: string; title: string }[];
  lines: (TimesheetLine & { weekday: string; editable: boolean; lockedLabel: string | null })[];
  totalDays: number;
  lastMonth: { month: string; label: string; toSign: boolean } | null;
};
export const halfOrWholeDay = z.union([z.literal(1), z.literal(0.5)]);
export const portalLineBody = z.object({ date: isoDate, activityId: uuid, days: halfOrWholeDay, work: textOrEmpty(LIMITS.workNote), place: workPlace });
export const portalLinePatch = z
  .object({ days: halfOrWholeDay.optional(), work: textOrEmpty(LIMITS.workNote).optional(), place: workPlace.optional(), date: isoDate.optional() })
  .strict();
export const signTimesheetBody = z.object({ confirm: z.literal(true) });
/**
 * EP-F2: the expert sends a timesheet back with one question per flagged line
 * (`lines`) and an optional general note (`text`). `lineId` with `text` is the
 * older one-line form (still accepted). At least one question.
 */
export const timesheetQuestionBody = z
  .object({
    text: textOrEmpty(4_000, "Question").default(""),
    lineId: uuid.nullable().optional(),
    lines: z.array(z.object({ lineId: uuid, text: text(2_000, "Question") })).max(62).default([]),
  })
  .refine((b) => b.text.length > 0 || b.lines.length > 0, { message: "Write your question.", path: ["text"] });
export type TimesheetQuestionInput = z.input<typeof timesheetQuestionBody>;
export type PortalInvoices = {
  rows: {
    id: Id;
    invoice: string;
    for: string;
    amountCents: number;
    sentAt: string | null;
    status: StatusChip;
    /** EX-N4: the team's note when it sent the invoice or claim back. */
    note?: string | null;
    /** EX-N4: where the expert corrects a sent-back invoice or claim and sends it again. */
    edit?: { label: string; path: string } | null;
    /** IX-F6 (feedback round 3): a partly approved claim: what was approved, and each item left out with why. */
    approvedCents?: number | null;
    notApproved?: { item: string; amountCents: number; reason: string }[];
  }[];
  months: { value: string; label: string; days: number; signed: boolean; disabled: boolean }[];
  feeCents: number | null;
};
export const portalInvoiceBody = z.object({
  month: isoMonth,
  number: text(60, "Invoice number"),
  amountCents: centsV3(LIMITS.budgetCentsMax),
  fileId: uuid,
  note: textOrEmpty(2_000),
});
export type ClaimOptions = {
  who: string;
  assignments: { activityId: Id; label: string; startDate: string | null; endDate: string | null; country: string | null }[];
};
/** EX-N4: a sent-back travel claim, as the claim form starts from when the expert corrects it. */
export type PortalClaimDraft = {
  id: Id;
  activityId: Id | null;
  travelFrom: string | null;
  travelTo: string | null;
  destination: string;
  destinationCountry: string | null;
  lines: { item: string; quantity: number; rateCents: number; receiptFileId: Id | null; receipt: { name: string; size: number } | null }[];
  note: string | null;
};
/** EP-F10: `activityId` takes the per-diem rate from that activity's approved ToR; `claimId` is the claim being corrected (not counted against itself). */
export const perDiemQuery = z.object({ country, from: isoDate, to: isoDate, activityId: uuid.optional(), claimId: uuid.optional() });
export type PerDiemAnswer = {
  rateCents: number | null;
  /** The days of the trip. */
  days: number;
  source: string;
  /** EP-F10: the days not in another claim of the expert yet (what the form starts with). Left out = `days`. */
  claimable?: number;
  /** EP-F10: "4 per diems for 19–22 Oct are already in your claim of 30 Oct (approved)", one per other claim. */
  alreadyClaimed?: string[];
};
export const claimLine = z.object({ item: text(200, "Item"), quantity: z.number().min(0).max(1000), rateCents: centsV3(LIMITS.unitCostCentsMax), receiptFileId: uuid.nullable() });
export const portalClaimBody = z.object({
  activityId: uuid,
  travelFrom: isoDate,
  travelTo: isoDate,
  destination: text(200, "Destination"),
  destinationCountry: country,
  lines: z.array(claimLine).min(1).max(50),
});
export type PortalDocumentRow = { id: Id; title: string; for: string; sentAt: string; status: StatusChip };
export type PortalDocument = {
  id: Id;
  title: string;
  for: string;
  status: StatusChip;
  steps: Steps;
  html: string;
  details: { name: string; address: string | null; ibanMasked: string | null };
  signedAt: string | null;
  // optional additions
  pdfUrl?: string;
  canSign?: boolean;
};
export const signDocumentBody = z.object({ agree: z.literal(true), typedName: text(LIMITS.userName, "Your full name") });
export type PortalDetails = { name: string; address: string | null; ibanMasked: string | null; ibanPending: boolean; vatNumber: string | null; phone: string | null };
export const portalDetailsPatch = z
  .object({
    name: text(LIMITS.userName, "Name").optional(),
    address: textOrEmpty(500).optional(),
    iban: z
      .string()
      .trim()
      .transform((s) => s.replace(/\s+/g, "").toUpperCase())
      .pipe(z.string().regex(/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/, "Enter a valid IBAN."))
      .optional(),
    vatNumber: textOrNull(40).optional(),
    phone: textOrNull(LIMITS.phone).optional(),
  })
  .strict();
export const portalMessageBody = z.object({
  subjectType: z.enum(["document", "timesheet", "engagement"]),
  subjectId: uuid.nullable(),
  body: text(10_000, "Message"),
});
export type PortalMessageResult = { id: Id; answeredBy: string };
export type PortalMessageContext = { context: string; contactName: string };
export type PortalCv = { current: FileRef | null; uploadedAt: string | null; usedIn: { torReference: string; title: string; sentAt: string }[] };
export const portalCvBody = z.object({ fileId: uuid, updateProfile: z.boolean() });
export type PortalCvResult = { current: FileRef; languages: string[]; expertise: string | null };
export const staffReplyBody = z.object({ body: text(10_000, "Reply") });
export type ExpertMessageRow = { id: Id; from: string; body: string; context: string; createdAt: string; answeredAt: string | null; side?: "expert" | "staff" };

/** Mod-97 check for an IBAN already normalised (no spaces, upper case). */
export function isValidIban(iban: string): boolean {
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(iban)) return false;
  const moved = iban.slice(4) + iban.slice(0, 4);
  let rest = 0;
  for (const ch of moved) {
    const code = ch >= "A" && ch <= "Z" ? String(ch.charCodeAt(0) - 55) : ch;
    for (const digit of code) rest = (rest * 10 + Number(digit)) % 97;
  }
  return rest === 1;
}
/** "•••• 4321" */
export const maskIban = (iban: string | null): string | null => (iban ? `•••• ${iban.slice(-4)}` : null);
